/**
 * Productionisation cohort wrapper for `GSNForContentSidebar`.
 *
 * Mounts the AGG-driven productionisation path. Unlike the legacy
 * experiment branch:
 *
 *   1. Receives an already-translated `InvitedAppShortcutsRecommendationsResult`
 *      from `InvitedAppShortcutsRecommendations.tsx` (Jira-owned renderer).
 *   2. Maps the resolved `recommendations` into the typed `recommendedApps`
 *      array used by the productionisation hook.
 *   3. Calls `useInvitedAppShortcuts({ mode: 'productionisation' })` to feed
 *      the cookie-write-only side of the legacy `InvitedAppShortcutsContainer`.
 *
 * Used only when:
 *   - `cloudId` + `userId` are non-empty
 *   - the productionisation cohort gate (`twcg-460-personalised-invite-notifications-m2`)
 *     resolves to `isEnabled === true` in the render-side dispatcher
 *   - the preload-side cohort gate (`twcg-460-preload-grs-invited-app-shortcuts(-gate)`)
 *     was on at preload time so `result` could be computed
 *
 * If the upstream preload fired but produced an error or empty result, the
 * `recommendedApps` list is `[]` and the productionisation hook treats it as
 * `NO_RECOMMENDED_APPS`, matching control behaviour.
 */
import React, { useEffect, useMemo, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { getJiraSsrCookieCacheInstance } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/ui/configurable-menu-items/global-shortcuts/useResolveInvitedAppShortcuts.tsx';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import type { ForYouConfig } from '@atlassian/global-side-navigation/for-you-types';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/common/types';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { toRecommendedProducts } from '@atlassian/cross-join-nudges/common/utils/invited-app-shortcuts-utils';
import type {
	InvitedAppShortcutAllowedSourceAppKey,
	InvitedAppShortcutRecommendedApp,
} from '@atlassian/cross-join-nudges/types';
import type { InvitedAppShortcutsRecommendationsResult } from '@atlassian/cross-join-nudges/ui/invited-app-shortcuts/invited-app-shortcuts-recommendations-provider';
import { useInvitedAppShortcuts } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	InvitedAppShortcutsSlot,
	InvitedAppShortcutsSlotContext,
	type InvitedAppShortcutsSlotConfig,
} from './InvitedAppShortcutsSlot.tsx';

const SOURCE_APP: InvitedAppShortcutAllowedSourceAppKey = 'jira';

type GsnComponentProps = ComponentPropsWithoutRef<typeof GlobalSideNavigation>;
type CustomizationStore = GsnComponentProps['customizationStore'];
type Items = GsnComponentProps['items'];
type NetworkStorageParams = GsnComponentProps['networkStorageParams'];

type ExperimentalOnShortcutsMenuSettled = GsnComponentProps['experimental_onShortcutsMenuSettled'];

type Props = {
	bottomElem: ReactNode | undefined;
	cloudId: string;
	customizationStore: CustomizationStore;
	// Mirrors the legacy experiment branch — see comment in the entrypoint
	// types file for rationale (UFO/impression analytics parity).
	experimental_onShortcutsMenuSettled: ExperimentalOnShortcutsMenuSettled;
	forYouConfig: ForYouConfig<ObjectListFlyoutItem>;
	intlMode: 'inherit';
	isShortcutsStatic: boolean;
	items: Items;
	networkStorageParams: NetworkStorageParams;
	peekMenuId: string | undefined;
	/**
	 * Translated recommendations result, produced by the Jira-owned renderer
	 * `InvitedAppShortcutsRecommendations.tsx` via
	 * `buildInvitedAppShortcutsRecommendationsResult`.
	 */
	result: InvitedAppShortcutsRecommendationsResult;
	shortcuts: ShortcutsConfig;
	testId: string;
	userId: string;
};

export function GSNForContentSidebarWithInvitedAppRecommendations(props: Props): React.JSX.Element {
	const {
		bottomElem,
		cloudId,
		customizationStore,
		experimental_onShortcutsMenuSettled: experimentalOnShortcutsMenuSettled,
		forYouConfig,
		intlMode,
		isShortcutsStatic,
		items,
		networkStorageParams,
		peekMenuId,
		result,
		shortcuts,
		testId,
		userId,
	} = props;

	// Map the translated `recommendations` into the typed recommended-app list.
	// `result.kind === 'error'` and `'empty'` both fall through to `[]`, which
	// the productionisation hook treats as `NO_RECOMMENDED_APPS`. This matches
	// control / disabled cohort behaviour visually.
	//
	// Keyed off the parsed `product`, NOT the raw `entityId`: since PUMA-2245 GRS
	// encodes the lozenge variant into the id (`confluence-animated-shortcut`), so
	// an entityId match would reject every recommendation. `product` is `null` for
	// ids that do not name a supported target app.
	const recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedApp> = useMemo(
		() =>
			result.kind === 'recommendations'
				? result.recommendations.reduce<InvitedAppShortcutRecommendedApp[]>((acc, r) => {
						if (r.product !== null) {
							acc.push({ product: r.product, variant: r.variant });
						}
						return acc;
					}, [])
				: [],
		[result],
	);

	// `targetProducts` is variant-agnostic by design, so map back down to plain
	// product keys rather than propagating the richer shape into it.
	const targetProducts = useMemo(() => toRecommendedProducts(recommendedApps), [recommendedApps]);

	// Reuse the same process-singleton cookie cache as the legacy experiment
	// branch (`useResolveInvitedAppShortcuts`). Productionisation mode does not
	// read from the cache for eligibility (that comes from `recommendedApps`),
	// but the hook's "Active in both modes" code paths
	// (`updateFromHydratedShortcuts`, `registerImpression`, `registerActivation`,
	// `updateHydratedProductLinks`, `updateFromAppAccessFilter`) still enqueue
	// writes to keep the cookie cache warm as a fallback against cohort flips /
	// gate-off. Passing the same singleton avoids divergent cache state.
	const resolverResult = useInvitedAppShortcuts({
		enabled: true,
		mode: 'productionisation',
		sourceProduct: SOURCE_APP,
		targetProducts,
		cloudId,
		userId,
		cache: getJiraSsrCookieCacheInstance(),
		recommendedApps,
	});

	// Destructure only the function we depend on: `resolverResult` is the full
	// `useInvitedAppShortcuts` return, whose `useMemo` includes a potentially
	// unstable `analytics` dep, so depending on the whole object could re-run the
	// effect (and re-fire the exposure) every render.
	const { fireFeatureExposed } = resolverResult;

	// Fire the productionisation-preload experiment exposure only when there are
	// recommendations to render — i.e. the user is genuinely exposed to the
	// treatment. Preloading is gated by the kill-switch `fg` in the parent
	// entrypoint (where eligibility isn't yet known); dispatch is gated on the m2
	// experiment in `GSNForContentSidebarEntryPoint.tsx`.
	useEffect(() => {
		if (recommendedApps.length > 0) {
			fireFeatureExposed({
				isEnabled: expVal('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', false),
			});
		}
	}, [recommendedApps, fireFeatureExposed]);

	// Config for GSN's `ShortcutsSlot` (`InvitedAppShortcutsSlot`), passed via
	// context so the slot keeps a stable identity. GSN receives the base
	// `shortcuts` config and the slot decorates its resolved `shortcutItems`.
	const slotConfig = useMemo<InvitedAppShortcutsSlotConfig>(
		() => ({
			mode: 'productionisation',
			resolverResult,
			cloudId,
			userId,
			recommendedApps,
			sourceApp: SOURCE_APP,
		}),
		[resolverResult, cloudId, userId, recommendedApps],
	);

	return (
		<InvitedAppShortcutsSlotContext.Provider value={slotConfig}>
			<GlobalSideNavigation
				app="jira"
				appLabel="Jira"
				bottomElem={bottomElem}
				cloudId={cloudId}
				customizationStore={customizationStore}
				experimental_onShortcutsMenuSettled={experimentalOnShortcutsMenuSettled}
				forYou={forYouConfig}
				intlMode={intlMode}
				isShortcutsStatic={isShortcutsStatic}
				items={items}
				networkStorageParams={networkStorageParams}
				peekMenuId={peekMenuId}
				shortcuts={shortcuts}
				testId={testId}
				ShortcutsSlot={InvitedAppShortcutsSlot}
			/>
		</InvitedAppShortcutsSlotContext.Provider>
	);
}
